"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createInstance = void 0;
const uuid_1 = require("uuid");
const create_error_1 = require("./create-error");
const catch_failed_to_fetch_1 = require("./logic/catch-failed-to-fetch");
const compose_send_1 = require("./logic/compose-send");
const default_transform_1 = require("./logic/default-transform");
const health_1 = require("./logic/health");
const network_monitor_1 = require("./logic/network-monitor");
const raw_send_1 = require("./logic/raw-send");
const throttle_1 = require("./logic/throttle");
const time_management_1 = require("./logic/time-management");
const track_network_state_1 = require("./logic/track-network-state");
const error_interfaces_1 = require("./types/error.interfaces");
const http_interfaces_1 = require("./types/http.interfaces");
const telemetry_logger_1 = require("./utilities/telemetry-logger");
/**
 * Creates an instance of Endpoints manager
 * @param appContext application context for Endpoint models
 * @param managerContext context for Endpoints manager
 */
function createInstance(appContext, managerContext) {
    const { globalContext } = managerContext;
    const cancellationMap = new Map();
    const throttleMap = new Map();
    // API for network monitoring
    const { networkMonitor, networkState } = (0, network_monitor_1.createNetworkMonitor)();
    const _send = (0, track_network_state_1.trackNetworkState)(networkMonitor, (0, catch_failed_to_fetch_1.catchFailedToFetch)((0, raw_send_1.rawSendConstructor)(globalContext, managerContext.configuration)));
    // create trackHealth send stack interceptor and provide healthApi
    const { trackHealth, ...health } = (0, health_1.createHealthApi)();
    // API for creating Endpoints API
    const createApi = (endpointsSetContructor) => createApiInstance(endpointsSetContructor, appContext, { ...managerContext, ...{ cancellationMap, trackHealth, throttleMap } }, _send);
    // API for cancelling requests
    const createCancelToken = () => {
        const token = (0, uuid_1.v4)();
        return {
            token,
            cancel: () => {
                const abortController = cancellationMap.get(token);
                abortController && abortController.abort();
                return !abortController;
            },
        };
    };
    return {
        createApi,
        createCancelToken,
        networkState,
        health,
    };
}
exports.createInstance = createInstance;
/**
 * Creates a strongly-typed data API based on provided interface
 * @param endpointsSetContructor - Models set contructor
 * @param appContext application context for Endpoint models
 * @param managerContext endpoints manager extended context
 * @param send - base send
 */
const createApiInstance = (endpointsSetContructor, appContext, managerContext, send) => {
    const api = {};
    const endpointsSet = endpointsSetContructor(appContext);
    const { baseUrlPromise, models } = endpointsSet;
    // Start resolving base URL early, to make it ready for transport calls
    baseUrlPromise && baseUrlPromise();
    // This implementation creates an executive function for ALL the models in endpoints Set.
    // The models set is constrained by API interface and may not have arbitrary properties.
    // The API is also constrained to have only Callable methods.
    // eslint-disable-next-line guard-for-in
    for (const key in models) {
        const endpoint = models[key];
        api[key] = createApiMethod(key, endpoint, endpointsSet, managerContext, send);
    }
    // TODO: Task# 1502694: start tracking set health here
    return api;
};
/**
 * Creates an executive function for API method
 * @param apiName name of api
 * @param endpointPromise endpoint model promise
 * @param endpointsSet endpoints set
 * @param managerContext endpoints manager context
 * @param send base send
 */
const createApiMethod = (apiName, endpointPromise, endpointsSet, managerContext, baseSend) => {
    const { trackHealth, throttleMap, globalContext } = managerContext;
    const throttleWrapper = (0, throttle_1.throttleFactory)(globalContext, endpointPromise, throttleMap);
    const { telemetryProvider } = managerContext;
    const { serviceName } = endpointsSet;
    return async (...args) => {
        let request;
        let serverResponse;
        let resultError;
        let parameters;
        const createdTime = new Date().toISOString();
        const requestStartTime = performance.now();
        try {
            // Initialize Endpoint model - it may timeout or throw an exception
            const endpoint = await (0, time_management_1.timed)(endpointPromise.catch((e) => {
                throw (0, create_error_1.createError)(error_interfaces_1.ErrorReason.ModelFailedToInitialize, undefined, e);
            }), globalContext, managerContext.configuration.modelInitializationTimeoutInMs, error_interfaces_1.ErrorReason.ModelTimeOut);
            // Compose Send from Endpoint model's properties
            let send = (0, compose_send_1.composeSend)(baseSend, endpoint, endpointsSet, managerContext);
            // Construct request with provided args
            request = endpoint.constructRequest(...args);
            request.operationLogger = (0, telemetry_logger_1.createOperationLogger)(request);
            // Add health tracking
            send = trackHealth(send, endpointsSet.serviceName, endpoint.healthPolicy);
            // Add throttler wrapper if ThrottlePolicy is defined
            send = throttleWrapper(send);
            try {
                // Create a telemetry string for representing data call arguments
                parameters = endpoint.customTelemetry?.(...args);
            }
            catch (e) {
                throw (0, create_error_1.createError)(error_interfaces_1.ErrorReason.CustomTelemetryFailed, undefined, e);
            }
            // Now execute the request, transform response and return the result.
            serverResponse = await send(request);
            return (0, default_transform_1.createTransform)(endpoint)(serverResponse);
        }
        catch (error) {
            if (error.serverResponse) {
                serverResponse = error.serverResponse;
            }
            resultError = error;
            throw error;
        }
        finally {
            const responseEndTime = performance.now();
            const duration = responseEndTime - requestStartTime;
            const correlationId = request?.correlationId;
            const status = serverResponse?.status;
            const statusString = (status && http_interfaces_1.Status[status]) || undefined;
            const operationLogs = request?.operationLogger.complete({ result: statusString, resultError }, serverResponse) || [];
            const result = resultError?.reason || statusString || "";
            telemetryProvider({
                apiName,
                serviceName,
                createdTime,
                parameters,
                duration,
                result,
                innerError: resultError?.innerException,
                operationLogs,
                correlationId,
            });
        }
    };
};
